<template>
  <section class="ma-4 ma-md-8">
    <t-page-title title="Locais" :on-add="() => $router.push({ name: 'create-locations' })" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="table"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
        >
          <template #[`item.created_at`]="{ item }">
            <span> {{ $luxon.DateTime.fromFormat(item.created_at, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy HH:mm:ss') }}</span>
          </template>
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.active`]="{ item }">
            <span> {{ item.active === true ? 'Ativo' : 'Inativo' }}</span>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { ref, useTemplateRef } from 'vue'
import TPageTitle from './../../components/TPageTitle.vue'
import LocalService from '@/services/LocalService'
import Local from '@/types/Local'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useProducerFilter } from '@/helpers/reports/producerFilter'
import { useSnackBar } from '@/stores/snackbar'
import { useDialog } from '@/stores/dialog'
import { useLoader } from '@/stores/loader'
import { Resources } from '@/plugins/permissions/typing'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'
import { DataTableHeader, DefaultDataTableActions } from '@/types/DataTable'
import DataTable from '@/components/datatable/DataTable.vue'
const resource: Resources = 'locations'

const { can } = useAbility()
const snackbar = useSnackBar()
const dialog = useDialog()
const loader = useLoader()
const router = useRouter()

const table = useTemplateRef('table')

const headers: DataTableHeader[] = [
  { title: 'ID', value: 'id', key: 'id', sortable: false },
  { title: 'Estado', value: 'state', key: 'state', sortable: false, align: 'center' },
  { title: 'Cidade', value: 'city', key: 'city', sortable: false },
  { title: 'Nome', value: 'name', key: 'name', sortable: false },
  { title: 'Criado em', value: 'created_at', key: 'created_at', sortable: false },
  { title: 'Status', value: 'active', key: 'active', sortable: false },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
]

const filters = ref([
  useTextFilter({ label: 'Nome', field: 'name', required: false }),
  useProducerFilter({ label: 'Produtor', field: 'producer_id', icon: 'far fa-shield' }),
])

const actions: DefaultDataTableActions = {
  update: {
    callback: (item: Local) =>
      router.push({
        name: 'edit-locations',
        params: { id: item.id },
      }),
    condition: () => can('update', 'locations'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: Local) => destroy(item),
    condition: () => can('delete', 'affiliates'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    return await (new LocalService()).index(page, filters, per_page)
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os Locais',
      'error',
    )

    throw error
  }
}

async function destroy(item: Local) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir o local? Essa ação é irreversível',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new LocalService()).destroy(item.id)

    snackbar.show(
      'Local excluído com sucesso.',
      'success',
    )

    if (table.value !== null) {
      table.value.fetch(1)
    }
  } finally {
    loader.decrement()
  }
}

</script>
